/* global console, window */

import { createNestablePublicClientApplication } from "@azure/msal-browser";

export function scopeForClientId(clientId) {
  return `api://${clientId}/access_as_user`;
}

// Contract with flows-gateway's identity gate (DEV-3362): flows-ui calls
// window.__reshapeGetIdentityToken itself — once on widget mount, and again
// if the user clicks its own "Sign in" retry button. Confirmed with the
// flows-ui side (apps/flows-ui/src/state/auth-state.tsx +
// build-initialize-body.ts) that resolving `null` on failure is safe: it's
// read with a plain truthiness check, identical to a rejection. No timeout —
// a real MS consent popup can legitimately take a while. Everything —
// client creation included — sits inside one try/catch so a failure at any
// stage resolves `null` instead of rejecting.
export async function setupIdentityGate(identityConfig) {
  if (!identityConfig) return;

  const { clientId } = identityConfig;
  const tokenRequest = { scopes: [scopeForClientId(clientId)] };

  // Cache the in-flight promise, not the resolved instance — caching only
  // after `await` let two calls issued before it settled each create their
  // own client. If it rejects, clear the cache before rethrowing: a bare
  // rejected promise is still truthy, so leaving it cached made one
  // transient init failure (e.g. a network blip) permanently break the
  // gate for the rest of the page session.
  let msalInstancePromise;
  function getMsalInstance() {
    if (!msalInstancePromise) {
      msalInstancePromise = createNestablePublicClientApplication({
        auth: { clientId, authority: "https://login.microsoftonline.com/common" },
        cache: { cacheLocation: "memoryStorage" },
      }).catch((error) => {
        msalInstancePromise = undefined;
        throw error;
      });
    }
    return msalInstancePromise;
  }

  // Only `errorCode` ever reaches console.warn — it's an enum-like MSAL
  // string (e.g. "no_account_error"), never the user's identity. Never log
  // the raw error object or an `account`/`result` — those carry the user's
  // email/UPN.
  function warn(message, error) {
    console.warn(message, error?.errorCode);
  }

  // "Retry" means "the previous call ended in failure" — NOT "this isn't
  // the first call ever". An ever-attempted flag misclassified a second
  // call after a SUCCESSFUL first one (a token refresh an hour later, a
  // remount) as a retry, sending it straight to the popup with no failure
  // to justify it. It also misclassified the second of two concurrent
  // calls at mount, since both read the flag before either settles.
  let lastAttemptFailed = false;
  window.__reshapeGetIdentityToken = async () => {
    const isRetry = lastAttemptFailed;

    try {
      const instance = await getMsalInstance();

      // Retry = the previous attempt failed, so this call is presumed to
      // be a real user gesture (flows-ui's own "Sign in" button, shown
      // only after a failure). Go straight to the interactive popup —
      // retrying acquireTokenSilent first would just repeat the failure,
      // and the extra await before acquireTokenPopup risks losing the
      // click's transient activation on hosts without the NAA broker (e.g.
      // Outlook on Mac/web), silently blocking the popup. The broker path
      // (the real target host) isn't affected either way.
      if (isRetry) {
        try {
          const result = await instance.acquireTokenPopup(tokenRequest);
          // So a later call in this same session can find the account via
          // acquireTokenSilent instead of always needing a popup.
          instance.setActiveAccount(result.account);
          lastAttemptFailed = false;
          return result.accessToken;
        } catch (popupError) {
          warn("ReshapeX sign-in: interactive sign-in failed, showing retry screen.", popupError);
          lastAttemptFailed = true;
          return null;
        }
      }

      // Not a retry (first call ever, or the previous one succeeded):
      // silent only — a popup here would be blocked by the browser anyway
      // without a user gesture.
      try {
        const result = await instance.acquireTokenSilent(tokenRequest);
        lastAttemptFailed = false;
        return result.accessToken;
      } catch (silentError) {
        warn("ReshapeX sign-in: silent sign-in failed, user will need to retry.", silentError);
        lastAttemptFailed = true;
        return null;
      }
    } catch (initError) {
      warn("ReshapeX sign-in: could not initialize sign-in, treating as signed out.", initError);
      lastAttemptFailed = true;
      return null;
    }
  };
}
